import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { BottomSheet } from "@/components/BottomSheet";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { localYmd } from "@/lib/format";
import { listCalendars } from "@/lib/calendarsApi";
import { LocationAutocomplete } from "@/components/LocationAutocomplete";
import { Field, MemberMultiPicker } from "./Shared";

export function CreateSheet({ open, tab, seedAt, onClose, onCreated }) {
  const { family, members, user } = useAuth();
  // Memoize `me` by user-id so child sheets receive a stable reference and
  // their `[me?.id]` effects don't fire on every parent render. (Fixes the
  // pre-existing "Maximum update depth exceeded" warnings spam on mount.)
  const me = React.useMemo(
    () => (members || []).find((m) => m.user_id === user?.id) || null,
    [members, user?.id],
  );
  if (!open || !family) return null;

  if (tab === "chores")
    return <ChoreCreateSheet onClose={onClose} onCreated={onCreated} familyId={family.id} members={members} me={me} />;
  if (tab === "polls")
    return <PollCreateSheet onClose={onClose} onCreated={onCreated} familyId={family.id} />;
  if (tab === "todos")
    return <TodoCreateSheet onClose={onClose} onCreated={onCreated} familyId={family.id} members={members} me={me} />;
  return <EventCreateSheet onClose={onClose} onCreated={onCreated} familyId={family.id} seedAt={seedAt} members={members} me={me} />;
}

/**
 * Quick-add for todos. Routed from the "+" button on the To-do tab —
 * fixes the bug where clicking + on To-do incorrectly opened the
 * "New event" sheet.
 *
 * Assignee defaults to "me" — the creator owns the to-do unless they
 * explicitly pick someone else. Tap a different chip to reassign.
 */
function TodoCreateSheet({ onClose, onCreated, familyId, members, me }) {
  const [lists, setLists] = React.useState([]);
  const [listId, setListId] = React.useState("");
  const [title, setTitle] = React.useState("");
  const [assigneeIds, setAssigneeIds] = React.useState(() => (me ? [me.id] : []));
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    api.get(`/families/${familyId}/todo-lists`).then(({ data }) => {
      setLists(data || []);
      if (data?.[0]) setListId(data[0].id);
    });
  }, [familyId]);

  const save = async (e) => {
    e.preventDefault();
    if (!title.trim() || !listId) return;
    setSaving(true); setError("");
    try {
      await api.post(`/families/${familyId}/todo-lists/${listId}/items`, {
        title: title.trim(),
        // Todos backend takes a single assignee_person_id. We send the
        // first member from the chips (usually "me").
        assignee_person_id: assigneeIds[0] || me?.id || undefined,
      });
      onCreated?.();
      onClose();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <BottomSheet open={true} onClose={onClose} title="New to-do">
      <form onSubmit={save} className="space-y-4 pb-2" data-testid="todo-create-form">
        <Field label="List" testid="todo-list-select">
          <select
            data-testid="todo-list-select-input"
            value={listId}
            onChange={(e) => setListId(e.target.value)}
            className="w-full bg-transparent border border-border rounded-xl px-3 py-2 text-[15px] focus:border-primary outline-none"
          >
            {lists.length === 0 && <option value="">No lists yet — create one first</option>}
            {lists.map((l) => (
              <option key={l.id} value={l.id}>{l.title || l.name}</option>
            ))}
          </select>
        </Field>
        <Field label="Title" testid="todo-title">
          <input
            data-testid="todo-title-input"
            autoFocus
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Buy milk"
            className="w-full bg-transparent border border-border rounded-xl px-3 py-2 text-[15px] focus:border-primary outline-none"
          />
        </Field>
        <Field label="For" testid="todo-assignee">
          <MemberMultiPicker
            members={members || []}
            selectedIds={assigneeIds.slice(0, 1)}
            // Todos are single-assignee server-side — when a user taps a
            // chip we REPLACE rather than toggle so only one stays active.
            onChange={(ids) => {
              if (ids.length === 0) { setAssigneeIds([]); return; }
              const newId = ids.find((id) => !assigneeIds.includes(id)) || ids[0];
              setAssigneeIds([newId]);
            }}
            testid="todo-assignee-picker"
          />
        </Field>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <button
          type="submit"
          data-testid="todo-save"
          disabled={!title.trim() || !listId || saving}
          className="w-full h-12 rounded-xl bg-foreground text-background text-sm font-medium tap disabled:opacity-50"
        >
          {saving ? "Saving…" : "Add to-do"}
        </button>
      </form>
    </BottomSheet>
  );
}

function EventCreateSheet({ onClose, onCreated, familyId, seedAt, members, me }) {
  const [title, setTitle] = useState("");
  // When user tapped a time slot in the Day view, pre-fill that exact
  // date+time. Otherwise default to today at 18:00 (the old default).
  const seed = seedAt ? new Date(seedAt) : null;
  const pad = (n) => String(n).padStart(2, "0");
  const [date, setDate] = useState(seed ? localYmd(seed) : localYmd(new Date()));
  const [time, setTime] = useState(seed ? `${pad(seed.getHours())}:${pad(seed.getMinutes())}` : "18:00");
  const [location, setLocation] = useState("");
  // Coords supplied by the LocationAutocomplete component when the user
  // picks a Nominatim suggestion. Powers the post-save geofence prompt.
  const [locationGeo, setLocationGeo] = useState(null);
  const [recurrenceRule, setRecurrenceRule] = useState("");
  const [participantIds, setParticipantIds] = useState(() => (me ? [me.id] : []));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // Sub-calendar picker (Batch 38). Defaults to the family-default.
  const [calendars, setCalendars] = useState([]);
  const [calendarId, setCalendarId] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cs = await listCalendars(familyId);
      if (cancelled) return;
      const live = (cs || []).filter((c) => !c.is_archived);
      setCalendars(live);
      const def = live.find((c) => c.is_default) || live[0];
      if (def) setCalendarId(def.id);
    })();
    return () => { cancelled = true; };
  }, [familyId]);

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      const startAt = new Date(`${date}T${time}:00`).toISOString();
      await api.post(`/families/${familyId}/events`, {
        title: title.trim(),
        start_at: startAt,
        category: "family",
        location_name: location.trim() || null,
        recurrence_rule: recurrenceRule || null,
        participant_ids: participantIds.length ? participantIds : (me ? [me.id] : []),
        calendar_id: calendarId || null,
      });
      // Auto-geofence prompt — only when the user picked a location with
      // coordinates AND we don't already have a USER-MANAGED place there.
      // Auto-tracked recents (created by /places/confirm during autocomplete)
      // are filtered out — they have category="other" + no alert_radius_m.
      // Soft, non-blocking: sonner toast with a Save action.
      if (locationGeo && locationGeo.lat != null && locationGeo.lng != null && location.trim()) {
        try {
          const { data: existing } = await api.get(`/families/${familyId}/places`);
          const lc = location.trim().toLowerCase();
          const isUserManagedMatch = (existing || []).some((p) => {
            const nameMatch = (p.name || "").toLowerCase() === lc
              || (p.aliases || []).some((a) => (a || "").toLowerCase() === lc);
            if (!nameMatch) return false;
            // Treat as "already saved" only if the user explicitly tagged
            // a category (not the auto "other") OR set an alert radius.
            const meaningfulCategory = p.category && p.category !== "other";
            const hasRadius = p.alert_radius_m && p.alert_radius_m > 0;
            return meaningfulCategory || hasRadius;
          });
          if (!isUserManagedMatch) {
            toast.message(`Save "${location.trim()}" as a place?`, {
              description: "Kinly will alert you on arrival + use it for late-detection nudges.",
              duration: 8000,
              action: {
                label: "Save",
                onClick: () => {
                  api.post(`/families/${familyId}/places`, {
                    name: location.trim(),
                    category: "other",
                    location: { lat: locationGeo.lat, lng: locationGeo.lng },
                    alert_radius_m: 150,
                    source: "kinly_detected",
                  }).then(() => toast.success("Place saved. Tweak the category in /you/profile."))
                    .catch(() => toast.error("Couldn't save place."));
                },
              },
            });
          }
        } catch { /* non-fatal */ }
      }
      onCreated?.();
      onClose();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <BottomSheet open={true} onClose={onClose} title="New event">
      <form onSubmit={onSave} className="space-y-5">
        <Field label="Title" testid="event-title">
          <input
            data-testid="event-title-input"
            autoFocus
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Family braai"
            required
            className="w-full bg-transparent border-b-2 border-border focus:border-primary py-2 outline-none text-[18px]"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Date" testid="event-date">
            <input
              data-testid="event-date-input"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full bg-transparent border-b-2 border-border focus:border-primary py-2 outline-none"
            />
          </Field>
          <Field label="Time" testid="event-time">
            <input
              data-testid="event-time-input"
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="w-full bg-transparent border-b-2 border-border focus:border-primary py-2 outline-none"
            />
          </Field>
        </div>
        <Field label="Location" testid="event-location">
          <LocationAutocomplete
            value={location}
            onChange={(name, geo) => {
              setLocation(name);
              if (geo && geo.lat != null) setLocationGeo(geo);
              else if (!name) setLocationGeo(null);
            }}
            placeholder="Home"
            testId="event-location-input"
          />
        </Field>
        {calendars.length > 1 && (
          <Field label="Calendar" testid="event-calendar">
            <div className="flex gap-2 flex-wrap mt-1" data-testid="event-calendar-picker">
              {calendars.map((c) => {
                const active = calendarId === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setCalendarId(c.id)}
                    data-testid={`event-cal-${c.id}`}
                    className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-medium tap border transition-colors ${
                      active
                        ? "bg-foreground text-background border-foreground"
                        : "bg-background text-foreground border-border"
                    }`}
                  >
                    <span
                      aria-hidden
                      className="inline-block w-2 h-2 rounded-full"
                      style={{ background: c.color }}
                    />
                    {c.name}
                  </button>
                );
              })}
            </div>
          </Field>
        )}
        <Field label="For" testid="event-participants">
          <MemberMultiPicker
            members={members || []}
            selectedIds={participantIds}
            onChange={setParticipantIds}
            testid="event-participants-picker"
          />
        </Field>
        <Field label="Repeats" testid="event-recurrence">
          <div className="flex flex-wrap gap-2 mt-1" data-testid="recurrence-picker">
            {[
              { id: "", label: "Once" },
              { id: "daily", label: "Daily" },
              { id: "weekdays", label: "Weekdays" },
              { id: "weekly", label: "Weekly" },
              { id: "monthly", label: "Monthly" },
            ].map((o) => (
              <button
                key={o.id || "once"}
                type="button"
                data-testid={`recurrence-${o.id || "once"}`}
                onClick={() => setRecurrenceRule(o.id)}
                className={[
                  "h-8 px-3 rounded-full border text-[12px] font-medium tap",
                  recurrenceRule === o.id
                    ? "border-foreground bg-foreground text-background"
                    : "border-border bg-card",
                ].join(" ")}
              >
                {o.label}
              </button>
            ))}
          </div>
        </Field>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <button
          data-testid="event-save"
          type="submit"
          disabled={!title.trim() || saving}
          className="w-full h-12 rounded-2xl bg-foreground text-background font-medium tap disabled:opacity-40"
        >
          {saving ? "Saving…" : "Add event"}
        </button>
      </form>
    </BottomSheet>
  );
}

function ChoreCreateSheet({ onClose, onCreated, familyId, members, me }) {
  const [title, setTitle] = useState("");
  const [assigneeIds, setAssigneeIds] = useState(() => (me ? [me.id] : []));
  // Due date+time are optional. Empty `date` means no due date set.
  const [date, setDate] = useState("");
  const [time, setTime] = useState("18:00");
  const [recurrenceRule, setRecurrenceRule] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      const due_at = date
        ? new Date(`${date}T${time || "18:00"}:00`).toISOString()
        : null;
      await api.post(`/families/${familyId}/chores`, {
        title: title.trim(),
        assignee_person_ids: assigneeIds.length ? assigneeIds : (me ? [me.id] : []),
        due_at,
        recurrence_rule: recurrenceRule || null,
      });
      onCreated?.();
      onClose();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  return (
    <BottomSheet open={true} onClose={onClose} title="New chore">
      <form onSubmit={onSave} className="space-y-5" data-testid="chore-create-form">
        <Field label="Title">
          <input
            data-testid="chore-title-input"
            autoFocus
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Unpack lunchbox"
            required
            className="w-full bg-transparent border-b-2 border-border focus:border-primary py-2 outline-none text-[18px]"
          />
        </Field>
        <Field label="For" testid="chore-assignee">
          <MemberMultiPicker
            members={members || []}
            selectedIds={assigneeIds}
            onChange={setAssigneeIds}
            testid="chore-assignee-picker"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Due date" testid="chore-due-date">
            <input
              data-testid="chore-due-date-input"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full bg-transparent border-b-2 border-border focus:border-primary py-2 outline-none"
            />
          </Field>
          <Field label="Time" testid="chore-due-time">
            <input
              data-testid="chore-due-time-input"
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              disabled={!date}
              className="w-full bg-transparent border-b-2 border-border focus:border-primary py-2 outline-none disabled:opacity-40"
            />
          </Field>
        </div>
        <Field label="Repeats" testid="chore-recurrence">
          <div className="flex flex-wrap gap-2 mt-1" data-testid="chore-recurrence-picker">
            {[
              { id: "", label: "Once" },
              { id: "daily", label: "Daily" },
              { id: "weekdays", label: "Weekdays" },
              { id: "weekly", label: "Weekly" },
              { id: "monthly", label: "Monthly" },
            ].map((o) => (
              <button
                key={o.id || "once"}
                type="button"
                data-testid={`chore-recurrence-${o.id || "once"}`}
                onClick={() => setRecurrenceRule(o.id)}
                className={[
                  "h-8 px-3 rounded-full border text-[12px] font-medium tap",
                  recurrenceRule === o.id
                    ? "border-foreground bg-foreground text-background"
                    : "border-border bg-card",
                ].join(" ")}
              >
                {o.label}
              </button>
            ))}
          </div>
        </Field>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <button
          data-testid="chore-save"
          type="submit"
          disabled={!title.trim() || saving}
          className="w-full h-12 rounded-2xl bg-foreground text-background font-medium tap disabled:opacity-40"
        >
          {saving ? "Saving…" : "Add chore"}
        </button>
      </form>
    </BottomSheet>
  );
}

function PollCreateSheet({ onClose, onCreated, familyId }) {
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const updateOpt = (i, v) => setOptions((o) => o.map((x, j) => (j === i ? v : x)));
  const addOpt = () => setOptions((o) => (o.length < 8 ? [...o, ""] : o));

  const onSave = async (e) => {
    e.preventDefault();
    const cleanOpts = options.map((o) => o.trim()).filter(Boolean);
    if (cleanOpts.length < 2) { setError("Need at least 2 options"); return; }
    setSaving(true); setError("");
    try {
      await api.post(`/families/${familyId}/polls`, {
        question: question.trim(),
        options: cleanOpts.map((label) => ({ label })),
      });
      onCreated?.();
      onClose();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  return (
    <BottomSheet open={true} onClose={onClose} title="New poll">
      <form onSubmit={onSave} className="space-y-5">
        <Field label="Question">
          <input
            data-testid="poll-question-input"
            autoFocus
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Where to eat Friday?"
            required
            className="w-full bg-transparent border-b-2 border-border focus:border-primary py-2 outline-none text-[18px]"
          />
        </Field>
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-medium mb-2">
            Options
          </p>
          <div className="space-y-2">
            {options.map((o, i) => (
              <input
                key={i}
                data-testid={`poll-option-${i}`}
                type="text"
                value={o}
                onChange={(e) => updateOpt(i, e.target.value)}
                placeholder={`Option ${i + 1}`}
                className="w-full bg-transparent border-b border-border focus:border-primary py-2 outline-none"
              />
            ))}
            {options.length < 8 && (
              <button
                type="button"
                data-testid="poll-add-option"
                onClick={addOpt}
                className="text-sm text-primary tap py-1"
              >
                + Add option
              </button>
            )}
          </div>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <button
          data-testid="poll-save"
          type="submit"
          disabled={!question.trim() || saving}
          className="w-full h-12 rounded-2xl bg-foreground text-background font-medium tap disabled:opacity-40"
        >
          {saving ? "Saving…" : "Post poll"}
        </button>
      </form>
    </BottomSheet>
  );
}
